import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppRoute from 'general/constants/AppRoute';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import Utils from 'general/utils/Utils';
import UIHelper from 'general/helpers/UIHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import PropTypes from 'prop-types';
import AppColors from 'general/constants/AppColors';

GroupRevenueView.propTypes = {
    agencyId: PropTypes.string,
    reconciliationDetail: PropTypes.object
};

const defaultColumnIds = ['STATION_CODE', 'STATION_NAME', 'STATUS', 'ENERGY_CAR', 'ENERGY_BIKE', 'AMOUNT'];

function GroupRevenueView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyId, reconciliationDetail } = props;
    const app = useSelector((state) => state.app);
    const { user } = useSelector((state) => state.auth);
    const [groupRevenueList, setGroupRevenueList] = useState([]);
    const [groupRevenueTotal, setGroupRevenueTotal] = useState(0);
    const [paginatedItems, setPaginatedItems] = useState([]);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'STATION_CODE',
                name: UIHelper.buildTableColumn('Mã trạm sạc'),
                selector: (row) => row?.stationCode,
                width: '140px',
                style: tableStyle.columns.normal,
                sortable: true,
            },
            {
                id: 'STATION_NAME',
                name: UIHelper.buildTableColumn('Tên trạm sạc'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            Utils.openInNewTab(`${AppRoute.stationList}/${row?.stationId}`);
                        }}
                    >
                        {row?.name}
                    </span>
                ),
                minWidth: '230px',
                style: tableStyle.columns.link,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái hoạt động'),
                minWidth: '150px',
                selector: (row) => {
                    const cs = reconciliationDetail?.metadata?.stations?.find(station => station?.stationId === row?.stationId);
                    return (
                        <KTLabel
                            text={ChargeHelper.Utils.getChargeStationStatusName(cs?.station?.currentStatus)}
                            isInline={true}
                            size={KTLabelSize.medium}
                            additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
                                cs?.station?.currentStatus
                            )}`}
                        />
                    )
                },
            },
            {
                id: 'ENERGY_CAR',
                name: UIHelper.buildTableColumn('Sản lượng điện sạc ô tô', 'kWh'),
                selector: (row) => Utils.formatNumber((row?.vehicleType?.CAR?.totalEnergy ?? 0) / 1000?.toFixed(3)),
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'ENERGY_BIKE',
                name: UIHelper.buildTableColumn('Đỗ xe quá giờ', 'Phút'),
                selector: (row) => Utils.formatNumber((row?.vehicleType?.BIKE?.totalEnergy ?? 0) / 1000?.toFixed(3)),
                style: tableStyle.columns.normal,
                minWidth: '200px',
                sortable: true,
                right: true,
            },
            {
                id: 'AMOUNT',
                name: UIHelper.buildTableColumn('Thành tiền', 'VNĐ'),
                selector: (row) => Utils.formatNumber(Math.round(row?.totalEnergyCommission ?? 0)),
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
        ];
    }, [t, reconciliationDetail]);

    useEffect(() => {
        if (reconciliationDetail) {
            setGroupRevenueList(reconciliationDetail?.metadata?.energyCommissionByStation ?? []);
            setGroupRevenueTotal(reconciliationDetail?.metadata?.energyCommissionByStation?.length ?? 0);
        }
    }, [reconciliationDetail])

    useEffect(() => {
        setPaginatedItems(groupRevenueList.slice(page * limit, (page + 1) * limit));
    }, [page, limit, groupRevenueList])

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // ---- methods ----

    return (
        <div>
            <div>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng doanh thu trạm sạc:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.round(_.sumBy(groupRevenueList, 'totalEnergyCommission')) ?? 0)} VNĐ
                </span>
            </div>
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={groupRevenueList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    hasActionColumn={false}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(groupRevenueList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={page + 1}
                        rowCount={groupRevenueTotal}
                        rowsPerPage={limit}
                        currentItemInPageCount={paginatedItems.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            setPage(iNewPage - 1);
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            setLimit(iNewRowsPerPage);
                            setPage(0);
                        }}
                        color={KTPaginationColor.success}
                        appendText="trạm sạc"
                    />
                </div>
            )}
        </div>
    );
}

export default GroupRevenueView;
